/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import MsDyn365 from '@msdyn365-commerce/core';
import { ICategoryUrl } from '@msdyn365-commerce/core-internal/dist/types/interfaces/ICategoryPathInterfaces';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent, NodeTag } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBreadcrumbData } from './breadcrumb.data';
import { IBreadcrumbProps, ILinksData } from './breadcrumb.props.autogenerated';
import { Link } from './components/link';
import { Separator } from './components/separator';

export interface IBreadcrumbViewProps extends IBreadcrumbProps<IBreadcrumbData> {
    root?: React.ReactNode;
    links?: React.ReactNode[];
    backButton?: React.ReactNode;
    separator?: React.ReactNode;
    BreadcrumbContainer: IModuleProps;
    BreadcrumbList: INodeProps;
    BreadcrumbListItem: INodeProps;
}

/**
 *
 * Breadcrumb component.
 * @extends {React.PureComponent<IBreadcrumbProps<IBreadcrumbData>>}
 */
class Breadcrumb extends React.PureComponent<IBreadcrumbProps<IBreadcrumbData>> {
    private readonly _pageType: string | undefined = this.props.context.request.urlTokens.pageType;

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry);

    public render(): JSX.Element | null {

        const { className } = this.props.config;
        let rootLink = null;
        let breadcrumbLinks = null;
        let backButtonComponent = null;
        rootLink = this._getRootLink();
        const itemPosition = rootLink ? 1 : 0;
        if (this._pageType === 'Category') {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
            if (!breadcrumbLinks) {
                breadcrumbLinks = this._getCategoryHierarchy(itemPosition);
            }
        } else if (this._pageType === 'Product') {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
            if (!breadcrumbLinks) {
                const breaCrumbType = this.props.context.app.config.breadcrumbType;
                if (breaCrumbType === 'category') {
                    breadcrumbLinks = this._getPDPLinks(itemPosition);
                }
                if (breaCrumbType === 'categoryAndBack') {
                    backButtonComponent = this._getPDPBackButton();
                    breadcrumbLinks = this._getPDPLinks(itemPosition);
                }
                if (breaCrumbType === 'back') {
                    backButtonComponent = this._getPDPBackButton();
                    if (backButtonComponent === null) {
                        breadcrumbLinks = this._getPDPLinks(itemPosition);
                    }
                }
            }

        } else {
            breadcrumbLinks = this._getCustomBreadcrumbLinks(itemPosition);
        }
        const linkSeparator = <Separator separator='/' />;

        const breadcrumbViewProps = {
            ...this.props,
            BreadcrumbContainer: {
                moduleProps: this.props,
                className: classnames('ms-breadcrumb', className),
                tag: 'nav',
                role: 'navigation'
            },
            BreadcrumbList: {
                tag: 'ol' as NodeTag,
                className: 'ms-breadcrumb_list'
            },
            BreadcrumbListItem: {
                tag: 'li',
                className: 'ms-breadcrumb_item'
            },
            root: rootLink,
            links: breadcrumbLinks,
            backButton: backButtonComponent,
            separator: linkSeparator
        };
        return this.props.renderView(breadcrumbViewProps) as React.ReactElement;
    }

    private readonly _getRootLink = (): JSX.Element | null => {
        const { root } = this.props.config;
        if (!root || !root.linkUrl || !root.linkUrl.destinationUrl) {
            this.props.context.telemetry.error('BreadCrumb link url content is empty, link wont render');
            return null;
        }
        return (
            <Link
                text={root.linkText}
                ariaLabel={root.ariaLabel || root.linkText}
                href={root.linkUrl && root.linkUrl.destinationUrl}
                index='1'
                className='ms-breadcrumb_link'
                telemetryContent={this.telemetryContent}
            />);

    };

    private readonly _getCustomBreadcrumbLinks = (itemPosition: number): JSX.Element[] | null => {
        const { links } = this.props.config;

        if (!links || links.length === 0) {
            this.props.context.telemetry.error('BreadCrumb links content is empty, links wont render');
            return null;
        }
        const defaultValueToSubtract = 1;
        return (
            links.map((value: ILinksData, index: number) => {
                itemPosition++;
                const keyIndex = index;
                return (index !== links.length - defaultValueToSubtract) ? (
                    <Link
                        key={index}
                        text={value.linkText}
                        ariaLabel={value.ariaLabel || value.linkText}
                        href={value.linkUrl && value.linkUrl.destinationUrl}
                        index={{ itemPosition }.toString()}
                        className='ms-breadcrumb_link'
                        telemetryContent={this.telemetryContent}
                    />
                ) : (
                    <div
                        key={keyIndex}
                        className='ms-breadcrumb_link ms-breadcrumb-no-link-text'
                    >
                        {value.linkText}
                    </div>
                );
            }));
    };

    private readonly _getCategoryHierarchy = (itemPosition: number): JSX.Element[] | null => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        if (!categoryHierarchy) {
            this.props.context.telemetry.error('Category Hierarchy content is empty, module wont render');
            return null;
        }
        return (
            categoryHierarchy.map((value: CategoryHierarchy, index: number) => {
                itemPosition++;

                const friendlyName = this._getFriendlyName(this.props.context.request.locale, value.NameTranslations);

                return (
                    <Link
                        key={index}
                        text={friendlyName}
                        ariaLabel={friendlyName}
                        href={value.Url}
                        index={itemPosition.toString()}
                        className='ms-breadcrumb_link'
                        noclick={index === categoryHierarchy.length - 1}
                        telemetryContent={this.telemetryContent}
                    />
                );
            }));

    };

    /**
     * The function to get translation name for the locale.
     * @param locale - The locale information.
     * @param nameTranslations - The name translations names.
     * @returns - The translation name for the locale.
     */
    private readonly _getFriendlyName = (locale: string, nameTranslations?: TextValueTranslation[]): string | undefined => {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation?.Text;
    };

    private readonly _getPDPLinks = (itemPosition: number): JSX.Element[] | null => {
        const { data, config } = this.props;
        const categoryHierarchy = data.categoryPaths.result;
        const product = data.product.result;
        const url = this.props.context.request.url.requestUrl.href;
        if (!categoryHierarchy) {
            this.props.context.telemetry.error('Category path content is empty for the product');
            return null;
        }
        const links: JSX.Element[] = [];

        categoryHierarchy.map((value: ICategoryUrl, index: number) => {
            itemPosition++;
            links.push(
                <Link
                    key={index}
                    text={value.Name}
                    ariaLabel={value.Name}
                    href={value.Url}
                    index={itemPosition.toString()}
                    className='ms-breadcrumb_link'
                    telemetryContent={this.telemetryContent}
                />
            );
        });
        if (product && !config.hideProductName) {
            itemPosition++;
            links.push(
                <Link
                    text={product.Name}
                    ariaLabel={product.Name}
                    href={url}
                    index={itemPosition.toString()}
                    noclick
                    className='ms-breadcrumb_link'
                    telemetryContent={this.telemetryContent}
                />
            );
        }
        return links;

    };

    private readonly _goBack = (): void => {
        window.history.back();
    };

    private readonly _getPDPBackButton = (): JSX.Element | null => {
        const isBackAllowed = this.props.context && this.props.context.request && this.props.context.request.query && this.props.context.request.query.back;
        if (!isBackAllowed) {
            return null;
        }
        if (MsDyn365.isBrowser && (document.referrer === '' || window.history.length < 2)) {
            return null;
        }
        return (
            <a
                className='ms-breadcrumb-back-button'
                role='button'
                href='javascript:void(0);'
                onClick={this._goBack}
                aria-label={this.props.resources.backToResultsText}
            >
                {this.props.resources.backToResultsText}
            </a>
        );

    };
}

export default Breadcrumb;
